(function() {
  'use strict';

  // Debug flag - set to true to enable debug logging
  const DEBUG = false;

  // Debug log function - only logs when DEBUG is true
  const debugLog = (...args) => {
    if (DEBUG) {
      console.log(...args);
    }
  };

  const logDebugJson = (json) => {
    if (DEBUG) {
      console.log('logDebugJson', json);
      console.log(JSON.stringify(json, null, 2));
    }
  };

  const ensureGlobal = () => {
    if (!window.RebuyCustom) window.RebuyCustom = {};
    if (!window.RebuyCustom.byWidgetId) window.RebuyCustom.byWidgetId = {};
    if (!window.RebuyCustom._lastHtml) window.RebuyCustom._lastHtml = {};
    if (!window.RebuyCustom.allowedWidgetIds) window.RebuyCustom.allowedWidgetIds = {};
    if (!window.RebuyCustom.bundleWidgets) window.RebuyCustom.bundleWidgets = {};
    return window.RebuyCustom;
  };

  // Helper function to get translation strings
  const getTranslation = (key, fallback = '') => {
    try {
      // Try variantStrings first (for addToCart, soldOut, preOrder, etc.)
      if (window.theme?.variantStrings && window.theme.variantStrings[key]) {
        return window.theme.variantStrings[key];
      }
      // Try strings.product (for view_details, release_date, etc.)
      if (window.theme?.strings?.product && window.theme.strings.product[key]) {
        return window.theme.strings.product[key];
      }
      // Try direct access with underscore (for release_date -> release_date)
      const underscoreKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      if (window.theme?.strings?.product && window.theme.strings.product[underscoreKey]) {
        return window.theme.strings.product[underscoreKey];
      }
    } catch (err) {
      debugLog('[Rebuy] getTranslation error:', err);
    }
    return fallback;
  };

  const getProductClassification = (product) => {
    const variants = Array.isArray(product?.variants) ? product.variants : [];

    const countdownValue = product?.metafields?.theme?.countdown;
    if (countdownValue != null && countdownValue !== '') {
      try {
        const countdownDate = new Date(countdownValue);
        const countdownUnix = countdownDate.getTime();
        const nowUnix = Date.now();
        if (countdownUnix > nowUnix) {
          return 'pre-order';
        }
      } catch (e) {
        console.warn('[Rebuy] Invalid countdown date:', countdownValue, e);
      }
    }

    if (variants.length === 2) {
      const titles = variants.map(v => (v?.title || '').trim().toUpperCase());
      if (titles.includes('PRE-OWNED') && titles.includes('NEW')) return 'preowned-new';
      return 'two-variants';
    }

    if (variants.length > 2) {
      return 'multiple';
    }

    return 'default';
  };

  const parseWidgetInfo = (widget) => {
    try {
      if (!widget || !widget.data) return;

      const widgetTitle = widget?.data?.settings?.language?.title || null;
      const g = ensureGlobal();
      const widgetId = widget?.data?.id || widget?.id || widget?.data?.widget_id;
      const widgetType = widget?.data?.config?.type || widget?.data?.settings?.type || null;

      if (widgetType === 'dynamic_bundle' || widgetTitle === 'Frequently Bought Together') {
        if (widgetId != null) {
          const widgetEl = document.querySelector(`[data-rebuy-id="${widgetId}"]`);
          widgetEl?.classList.add('rebuy-custom-frequently-bought-together-ui');
          g.bundleWidgets[widgetId] = widget;
          Promise.all((widget.data.products || []).map(loadMarketPrices)).then(() => {
            g._scheduleWidgetScan?.();
          }).catch(err => console.warn('[Rebuy] Could not load bundle market prices:', err));
        }
        debugLog(`[Rebuy] Products widget id ${widgetId}:`, widget);

        return;
      }

      if (widgetTitle !== 'Similar Products' && widgetType !== 'product') {
        debugLog(`[Rebuy] Similar Products widget id ${widgetId}:`, widget);
        return;
      }


      const products = Array.isArray(widget.data.products) ? widget.data.products : [];
      if (!products.length) return;

      const classified = products.map(p => ({
        ...p,
        classification: getProductClassification(p)
      }));
      if (widgetId != null) {
        const prev = g.byWidgetId[widgetId] || {};
        g.byWidgetId[widgetId] = {
          ...prev,
          title: widgetTitle,
          products,
          classified
        };
        g.allowedWidgetIds[widgetId] = true;
      }

      g.similarProducts = classified;
      g.similarProductsRaw = products;
      Promise.all(products.map(loadMarketPrices)).then(() => {
        g._scheduleWidgetScan?.();
      }).catch(err => console.warn('[Rebuy] Could not load market prices:', err));
    } catch (err) {
      console.error('[Rebuy] parseWidgetInfo error:', err);
    }
  };

  const getWidgetFromEvent = (e) => {
    const d = e && e.detail ? e.detail : {};
    return d.widget || d.el || d.element || d.container || d;
  };

  const logAndParse = (name) => (e) => {
    if (window.Rebuy && window.Rebuy) {
      debugLog(`[Rebuy] Rebuy object found:`, window.Rebuy);
    }
    const widget = getWidgetFromEvent(e);
    if (widget) parseWidgetInfo(widget);
  };

  ['rebuy.beforeReady', 'rebuy.ready', 'rebuy.productsChange'].forEach(eventName => {
    document.addEventListener(eventName, logAndParse(eventName));
  });

  const addToCart = async (variantId, buttonElement) => {
    if (!variantId) {
      console.error('[Rebuy] No variant ID provided');
      return;
    }

    if (!buttonElement) return;

    const btnText = buttonElement.querySelector('.product-card__btn-text');
    const originalText = btnText ? btnText.innerHTML : '';

    buttonElement.setAttribute('aria-disabled', 'true');
    buttonElement.setAttribute('aria-busy', 'true');

    if (btnText) {
      btnText.innerHTML = '<span class="rebuy-loading-dots"><span></span><span></span><span></span></span>';
    }

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const config = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    };

    try {
      const response = await fetch('/cart/add.js', config);
      const parsedState = await response.json();

      if (parsedState.status) {
        console.error('[Rebuy] Add to cart error:', parsedState);
        if (window.theme && window.theme.pubsub) {
          window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartError, {
            source: 'rebuy-product-card',
            productVariantId: variantId,
            errors: parsedState.errors || parsedState.description,
            message: parsedState.message
          });
        }

        if (btnText) {
          btnText.innerHTML = originalText;
        }
        buttonElement.removeAttribute('aria-busy');
        buttonElement.removeAttribute('aria-disabled');
        return;
      }

      if (window.theme && window.theme.pubsub) {
        window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'rebuy-product-card',
          productVariantId: variantId,
        });
      }

      if (btnText) {
        btnText.innerHTML = originalText;
      }
      buttonElement.removeAttribute('aria-busy');
      buttonElement.removeAttribute('aria-disabled');

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.show) {
        cartDrawer.show(buttonElement);
      }
    } catch (error) {
      console.error('[Rebuy] Add to cart fetch error:', error);
      if (btnText) {
        btnText.innerHTML = originalText;
      }
      buttonElement.removeAttribute('aria-busy');
      buttonElement.removeAttribute('aria-disabled');
    }
  };

  const formatCountdown = (countdownStr) => {
    try {
      const date = new Date(countdownStr);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const formattedDate = months[date.getMonth()] + ' ' + date.getDate() + ', ' + date.getFullYear();
      return formattedDate;
    } catch (e) {
      console.warn('[Rebuy] formatCountdown error:', e);
      return countdownStr;
    }
  };

  // Shopify's Ajax product API returns prices in the customer's presentment
  // currency, in the same units consumed by the theme's money formatter.
  const marketPrices = new Map();
  const marketPriceKey = (product) => JSON.stringify([
    window.Shopify?.routes?.root || '/', window.Shopify?.currency?.active, product.handle
  ]);

  const loadMarketPrices = (product) => {
    const key = marketPriceKey(product);
    if (marketPrices.has(key)) return marketPrices.get(key).promise;

    const entry = {};
    marketPrices.set(key, entry);
    entry.promise = (async () => {
      const root = window.Shopify?.routes?.root || '/';
      const currency = window.Shopify?.currency?.active;
      const path = 'products/' + encodeURIComponent(product.handle) + '.js' +
        (currency ? '?currency=' + encodeURIComponent(currency) : '');
      let response = await fetch(root + path);
      // Some translated storefront routes do not expose the original handle.
      // The root endpoint keeps the same customer currency/session.
      if (response.status === 404 && root !== '/') response = await fetch('/' + path);
      if (!response.ok) throw new Error('Shopify product pricing: ' + response.status);
      const data = await response.json();
      if (String(data.id) !== String(product.id) || !Array.isArray(data.variants) ||
          !data.variants.length || data.variants.some(v => !Number.isFinite(v.price))) {
        throw new Error('Invalid Shopify product pricing');
      }
      entry.product = data;
      return data;
    })().catch(err => {
      marketPrices.delete(key);
      throw err;
    });
    return entry.promise;
  };

  const getMarketVariant = (product, variant) => {
    const localized = product && marketPrices.get(marketPriceKey(product))?.product;
    if (!localized) return null;
    return variant
      ? localized.variants.find(v => String(v.id) === String(variant.id))
      : localized.variants.reduce((lowest, v) => v.price < lowest.price ? v : lowest);
  };

  const formatMarketAmount = (amount) => {
    const settings = window.theme.settings;
    return window.theme.Currency.formatMoney(amount,
      settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat);
  };

  const formatPrice = (product, variant, compare = false) => {
    const selected = getMarketVariant(product, variant);
    if (!selected) return '';
    const amount = compare ? selected.compare_at_price : selected.price;
    if (compare && !(amount > selected.price)) return '';
    return formatMarketAmount(amount);
  };

  const getBundlePrices = (widget) => {
    const items = [];
    let total = 0;
    let compareTotal = 0;
    for (const product of widget.data.products || []) {
      // A bundle uses the chosen variant, never the product's minimum price.
      if (!product.selected_variant) return null;
      const variant = getMarketVariant(product, product.selected_variant);
      if (!variant) return null;
      const quantity = Number(product.quantity ?? 1);
      if (!Number.isInteger(quantity) || quantity < 0) return null;
      const comparePrice = Math.max(variant.price, variant.compare_at_price || 0);
      items.push({ product, price: variant.price, comparePrice });
      if (product.selected) {
        total += variant.price * quantity;
        compareTotal += comparePrice * quantity;
      }
    }
    return { items, total, compareTotal };
  };

  const buildVariantButton = (variant, href, product) => {
    if (!variant) return '';

    const variantTitle = variant.title || '';
    const variantPrice = formatPrice(product, variant) || '';
    const variantCompareAt = formatPrice(product, variant, true);
    const variantId = variant?.id || '';
    const variantIdAttr = variantId ? `data-variant-id="${variantId}"` : '';

    const inventory = variant?.inventory_quantity ?? (variant?.inventory_management !== null ? 0 : null);
    const isSoldOut = inventory !== null && inventory === 0;

    if (isSoldOut) {
      const soldOutLabel = getTranslation('soldOut', 'SOLD OUT');
      return '<div class="button button--primary rebuy-sold-out-btn flex justify-center items-center w-full">' +
        '<div class="product-card__btn-text" style="font-weight: normal; display: flex; flex-direction: column; align-items: center; gap: 0.25rem;"><div style="font-size: 1rem;">' + variantTitle.replace(/</g, '&lt;') + '</div><div style="font-size: 1.1em; font-weight: 700;">' + soldOutLabel + '</div></div>' +
      '</div>';
    }

    const priceHtml = (variantPrice || variantCompareAt)
      ? '<div style="font-size: 0.9em;">' + (variantPrice ? '<span class="price__regular">' + variantPrice.replace(/</g, '&lt;') + '</span>' : '') + (variantCompareAt ? ' <span class="price__compare" style="font-size: 0.85em; margin-left: 4px;">' + variantCompareAt.replace(/</g, '&lt;') + '</span>' : '') + '</div>'
      : '';

    return '<button type="button" class="button button--primary hover-button animated-button rebuy-add-to-cart-btn flex justify-center items-center" ' + variantIdAttr + '>' +
      '<div class="product-card__btn-text" style="font-weight: normal; display: flex; flex-direction: column; align-items: center; gap: 0.1rem; font-size: 1rem;"><div>' + variantTitle.replace(/</g, '&lt;') + '</div>' + priceHtml + '</div>' +
    '</button>';
  };

  const buildFormattedCard = (blockEl, widgetId) => {
    let productId = null;
    const classList = Array.from(blockEl.classList || []);
    const productIdClass = classList.find(c => c.startsWith('product-id-'));
    if (productIdClass) {
      productId = productIdClass.replace('product-id-', '');
    } else {
      productId = blockEl.getAttribute('data-product-id') || blockEl.getAttribute('data-rebuy-product-id') || null;
    }

    const g = ensureGlobal();
    const widgetData = g.byWidgetId[widgetId] || {};
    const productMetadata = widgetData.classified?.find(p => String(p.id) === String(productId)) ||
                            widgetData.products?.find(p => String(p.id) === String(productId)) || null;

    debugLog(`[Rebuy] buildFormattedCard: ${widgetId} - ${productId}`, {
      widgetId,
      productId,
      blockClasses: blockEl.className,
      productMetadata: productMetadata,
      html: blockEl.innerHTML,
    });

    const mediaLink = blockEl.querySelector('.rebuy-product-media a');
    const mediaImg = blockEl.querySelector('.rebuy-product-media img');
    const titleLink = blockEl.querySelector('.rebuy-product-title-link');
    const addBtn = blockEl.querySelector('.rebuy-product-actions button');

    const href = mediaLink?.getAttribute('href') || titleLink?.getAttribute('href') || '#';
    const imgSrc = mediaImg?.getAttribute('src') || '';
    const imgAlt = mediaImg?.getAttribute('alt') || '';
    const title = titleLink?.textContent?.trim() || '';
    const vendor = productMetadata?.vendor || '';
    const btnText = addBtn?.textContent?.trim() || getTranslation('addToCart', 'Add to Cart');

    let buttonsHtml = '';
    let preOrderSoldOut = false;
    if (productMetadata?.classification === 'preowned-new' && productMetadata?.variants) {
      const preownedVariant = productMetadata.variants.find(v => v.title?.toUpperCase() === 'PRE-OWNED');
      const newVariant = productMetadata.variants.find(v => v.title?.toUpperCase() === 'NEW');

      const variantButtons = [];
      productMetadata.variants.forEach((variant) => {
        const variantTitleUpper = variant?.title?.toUpperCase() || '';
        if (variantTitleUpper === 'NEW' || variantTitleUpper === 'PRE-OWNED') {
          const variantHref = variant?.link || href;
          const buttonHtml = buildVariantButton(variant, variantHref, productMetadata);
          variantButtons.push(buttonHtml);
        }
      });

      buttonsHtml = '<div class="product-card__buttons flex justify-between flex-wrap gap-2 w-full" style="gap: 6px;">' +
        variantButtons.join('') +
      '</div>';
    } else if (productMetadata?.classification === 'two-variants' && productMetadata?.variants && productMetadata.variants.length === 2) {
      const variant1 = productMetadata.variants[0];
      const variant2 = productMetadata.variants[1];
      const variant1Title = variant1?.title || '';
      const variant2Title = variant2?.title || '';
      const variant1Price = formatPrice(productMetadata, variant1) || '';
      const variant1CompareAt = formatPrice(productMetadata, variant1, true);
      const variant2Price = formatPrice(productMetadata, variant2) || '';
      const variant2CompareAt = formatPrice(productMetadata, variant2, true);
      const variant1Href = variant1?.link || href;
      const variant2Href = variant2?.link || href;

      const variant1Inventory = variant1?.inventory_quantity ?? (variant1?.inventory_management !== null ? 0 : null);
      const variant2Inventory = variant2?.inventory_quantity ?? (variant2?.inventory_management !== null ? 0 : null);
      const variant1SoldOut = variant1Inventory !== null && variant1Inventory === 0;
      const variant2SoldOut = variant2Inventory !== null && variant2Inventory === 0;
      const bothSoldOut = variant1SoldOut && variant2SoldOut;

      if (bothSoldOut) {
        const soldOutLabel = getTranslation('soldOut', 'SOLD OUT');
        const variant1TitleEscaped = variant1Title.replace(/</g, '&lt;');
        const variant2TitleEscaped = variant2Title.replace(/</g, '&lt;');

        buttonsHtml = '<div class="product-card__buttons flex justify-between flex-wrap gap-2 w-full" style="gap: 6px;">' +
          '<div class="button button--primary rebuy-sold-out-btn flex justify-center items-center w-full">' +
            '<div class="product-card__btn-text" style="font-weight: normal; display: flex; flex-direction: column; align-items: center; gap: 0.25rem;"><div style="font-size: 0.85rem;">' + variant1TitleEscaped + '</div><div style="font-size: 1.1em; font-weight: 700;">' + soldOutLabel + '</div></div>' +
          '</div>' +
          '<div class="button button--primary rebuy-sold-out-btn flex justify-center items-center w-full">' +
            '<div class="product-card__btn-text" style="font-weight: normal; display: flex; flex-direction: column; align-items: center; gap: 0.25rem;"><div style="font-size: 0.85rem;">' + variant2TitleEscaped + '</div><div style="font-size: 1.1em; font-weight: 700;">' + soldOutLabel + '</div></div>' +
          '</div>' +
        '</div>';
      } else {
        let variant1Btn = '';
        const variant1Id = variant1?.id || '';
        const variant1IdAttr = variant1Id ? `data-variant-id="${variant1Id}"` : '';

        if (variant1SoldOut) {
          variant1Btn = '<div class="button button--primary rebuy-sold-out-btn flex justify-center items-center w-full">' +
            '<div class="product-card__btn-text" style="font-weight: normal; display: flex; flex-direction: column; align-items: center; gap: 0.25rem;"><div style="font-size: 0.85rem;">' + variant1Title.replace(/</g, '&lt;') + '</div><div style="font-size: 1.1em; font-weight: 700;">' + getTranslation('soldOut', 'SOLD OUT') + '</div></div>' +
          '</div>';
        } else {
          const variant1PriceHtml = (variant1Price || variant1CompareAt)
            ? '<div style="font-size: 1.1rem;">' + (variant1Price ? '<span class="price__regular">' + variant1Price.replace(/</g, '&lt;') + '</span>' : '') + (variant1CompareAt ? ' <span class="price__compare" style="font-size: 0.9em; margin-left: 4px;">' + variant1CompareAt.replace(/</g, '&lt;') + '</span>' : '') + '</div>'
            : '';
          variant1Btn = '<button type="button" class="button button--primary hover-button animated-button rebuy-add-to-cart-btn flex justify-center items-center" ' + variant1IdAttr + '>' +
            '<div class="product-card__btn-text" style="font-weight: normal; display: flex; flex-direction: column; align-items: center; gap: 0.1rem; font-size: 0.85rem;"><div>' + variant1Title.replace(/</g, '&lt;') + '</div>' + variant1PriceHtml + '</div>' +
          '</button>';
        }

        let variant2Btn = '';
        const variant2Id = variant2?.id || '';
        const variant2IdAttr = variant2Id ? `data-variant-id="${variant2Id}"` : '';

        if (variant2SoldOut) {
          variant2Btn = '<div class="button button--primary rebuy-sold-out-btn flex justify-center items-center w-full">' +
            '<div class="product-card__btn-text" style="font-weight: normal; display: flex; flex-direction: column; align-items: center; gap: 0.25rem;"><div style="font-size: 0.85rem;">' + variant2Title.replace(/</g, '&lt;') + '</div><div style="font-size: 1.1em; font-weight: 700;">' + getTranslation('soldOut', 'SOLD OUT') + '</div></div>' +
          '</div>';
        } else {
          const variant2PriceHtml = (variant2Price || variant2CompareAt)
            ? '<div style="font-size: 1.1rem;">' + (variant2Price ? '<span class="price__regular">' + variant2Price.replace(/</g, '&lt;') + '</span>' : '') + (variant2CompareAt ? ' <span class="price__compare" style="font-size: 0.9em; margin-left: 4px;">' + variant2CompareAt.replace(/</g, '&lt;') + '</span>' : '') + '</div>'
            : '';
          variant2Btn = '<button type="button" class="button button--primary hover-button animated-button rebuy-add-to-cart-btn flex justify-center items-center" ' + variant2IdAttr + '>' +
            '<div class="product-card__btn-text" style="font-weight: normal; display: flex; flex-direction: column; align-items: center; gap: 0.1rem; font-size: 0.85rem;"><div>' + variant2Title.replace(/</g, '&lt;') + '</div>' + variant2PriceHtml + '</div>' +
          '</button>';
        }

        buttonsHtml = '<div class="product-card__buttons flex justify-between flex-wrap gap-2 w-full" style="gap: 6px;">' +
          variant1Btn +
          variant2Btn +
        '</div>';
      }
    } else if (productMetadata?.classification === 'pre-order') {
      const selectedVariant = productMetadata.selected_variant || productMetadata.variants?.[0];
      const preOrderPrice = formatPrice(productMetadata, selectedVariant) || '';
      const preOrderCompareAt = formatPrice(productMetadata, selectedVariant, true);
      const preOrderHref = selectedVariant?.link || href;
      const preOrderLabel = getTranslation('preOrder', 'PRE-ORDER').toUpperCase();

      const preOrderInventory = selectedVariant?.inventory_quantity ?? (selectedVariant?.inventory_management !== null ? 0 : null);
      preOrderSoldOut = preOrderInventory !== null && preOrderInventory === 0;

      let releaseDateHtml = '';
      const countdownValue = productMetadata?.metafields?.theme?.countdown;
      if (countdownValue != null && countdownValue !== '') {
        const formattedCountdown = formatCountdown(countdownValue);
        const countdownDisplay = String(formattedCountdown).replace(/</g, '&lt;');
        releaseDateHtml = '<div class="product-card__btn-text" style="text-align: center; font-size: 1rem;"><div style="font-size: 0.85rem;">' + getTranslation('release_date', 'RELEASE DATE') + '</div><div style="font-size: 1rem; font-weight: 700;">' + countdownDisplay + '</div></div>';
      } else {
        releaseDateHtml = '<div class="product-card__btn-text" style="text-align: center; font-size: 1rem;"><div style="font-size: 0.85rem;">' + getTranslation('release_date', 'RELEASE DATE') + '</div><div style="font-size: 1rem; font-weight: 700;">TBA</div></div>';
      }

      if (preOrderSoldOut) {
        buttonsHtml = '<div class="product-card__buttons flex justify-between flex-wrap gap-2 w-full" style="gap: 6px;">' +
          '<div class="button button--primary rebuy-sold-out-btn flex justify-center items-center w-full">' +
            '<div class="product-card__btn-text" style="font-weight: normal; display: flex; flex-direction: column; align-items: center; gap: 0.25rem;"><div style="font-size: 0.85rem;">' + preOrderLabel + '</div><div style="font-size: 1.1em; font-weight: 700;">' + getTranslation('soldOut', 'SOLD OUT') + '</div></div>' +
          '</div>' +
          '<div class="button button--primary rebuy-release-date-btn flex justify-center items-center w-full">' +
            releaseDateHtml +
          '</div>' +
        '</div>';
      } else {
        const preOrderPriceHtml = (preOrderPrice || preOrderCompareAt)
          ? '<div style="font-size: 1.1rem;">' + (preOrderPrice ? '<span class="price__regular">' + preOrderPrice.replace(/</g, '&lt;') + '</span>' : '') + (preOrderCompareAt ? ' <span class="price__compare" style="font-size: 0.9em; margin-left: 4px;">' + preOrderCompareAt.replace(/</g, '&lt;') + '</span>' : '') + '</div>'
          : '';
        buttonsHtml = '<div class="product-card__buttons flex justify-between flex-wrap gap-2 w-full" style="gap: 6px;">' +
          '<button type="button" class="button button--primary hover-button animated-button rebuy-add-to-cart-btn flex justify-center items-center" data-variant-id="' + (selectedVariant?.id || '') + '">' +
            '<div class="product-card__btn-text" style="font-weight: normal; display: flex; flex-direction: column; align-items: center; gap: 0.1rem; font-size: 0.85rem;"><div>' + preOrderLabel + '</div>' + preOrderPriceHtml + '</div>' +
          '</button>' +
          '<div class="button button--primary rebuy-release-date-btn flex justify-center items-center" style="width: 48.5%;">' +
            releaseDateHtml +
          '</div>' +
        '</div>';
      }
    } else {
      const isMultiple = productMetadata?.classification === 'multiple' ||
                         (productMetadata?.variants && productMetadata.variants.length > 2);

      // Choose the lowest localized price across the Shopify variants.
      const lowestPrice = isMultiple
        ? formatPrice(productMetadata, null)
        : '';

      if (isMultiple) {
        buttonsHtml = '<div class="product-card__buttons flex justify-between  flex-col  flex-wrap gap-2 w-full">' +
          '<a href="' + href + '" class="button button--primary hover-button animated-button flex justify-center items-center">' +
            '<div class="product-card__btn-text">' + getTranslation('view_details', 'View Detail') + (lowestPrice ? ' <span class="price__regular" style="margin-left:9px;">' + lowestPrice.replace(/</g, '&lt;') + '</span>' : '') + '</div>' +
          '</a>' +
        '</div>';
      } else {
        // Default: Add to Cart button with variant ID
        const selectedVariant = productMetadata?.selected_variant || productMetadata?.variants?.[0];
        const variantId = selectedVariant?.id || '';
        const variantIdAttr = variantId ? `data-variant-id="${variantId}"` : '';
        // Format the selected variant using Shopify market pricing
        const variantPrice = formatPrice(productMetadata, selectedVariant);
        const variantCompareAt = formatPrice(productMetadata, selectedVariant, true);

        buttonsHtml = '<div class="product-card__buttons flex justify-between  flex-col  flex-wrap gap-2 w-full">' +
          '<button type="button" class="button button--primary hover-button animated-button rebuy-add-to-cart-btn flex justify-center items-center w-full" ' + variantIdAttr + '>' +
            '<div class="product-card__btn-text">' + btnText.replace(/</g, '&lt;') + (variantPrice ? ' <span class="price__regular" style="margin-left:9px;">' + variantPrice.replace(/</g, '&lt;') + '</span>' : '') + (variantCompareAt ? ' <span class="price__compare" style="font-size: 0.9rem; margin-left: 4px;">' + variantCompareAt.replace(/</g, '&lt;') + '</span>' : '') + '</div>' +
          '</button>' +
        '</div>';
      }
    }

    const isPreOrder = productMetadata?.classification === 'pre-order';
    const isTwoVariantsBothSoldOut = productMetadata?.classification === 'two-variants' &&
                                      productMetadata?.variants &&
                                      productMetadata.variants.length === 2 &&
                                      (() => {
                                        const v1 = productMetadata.variants[0];
                                        const v2 = productMetadata.variants[1];
                                        const v1Inv = v1?.inventory_quantity ?? (v1?.inventory_management !== null ? 0 : null);
                                        const v2Inv = v2?.inventory_quantity ?? (v2?.inventory_management !== null ? 0 : null);
                                        const v1SoldOut = v1Inv !== null && v1Inv === 0;
                                        const v2SoldOut = v2Inv !== null && v2Inv === 0;
                                        return v1SoldOut && v2SoldOut;
                                      })();

    let cardClass = 'card product-card product-card--card flex flex-col leading-none relative';
    if (isPreOrder) {
      cardClass += ' rebuy-pre-order-card';
    } else if (isTwoVariantsBothSoldOut) {
      cardClass += ' rebuy-two-variants-sold-out-card';
    }

    // Check if product has NSFW content
    const isNsfw = productMetadata?.tags?.includes('Exclusive_18+');
    const nsfwThumbnailUrl = window.theme?.assets?.nsfwThumbnailUrl || '';

    let nsfwOverlayHtml = '';
    if (isNsfw && nsfwThumbnailUrl) {
      nsfwOverlayHtml = '<div class="nsfw-overlay absolute top-0 left-0 w-full h-full z-10 flex flex-col items-center justify-center cursor-pointer" ' +
        'onclick="event.preventDefault(); event.stopPropagation(); sessionStorage.setItem(\'nsfw_revealed\', \'true\'); document.documentElement.classList.add(\'nsfw-revealed\');">' +
        '<img src="' + nsfwThumbnailUrl + '" alt="NSFW Content" class="nsfw-overlay__image" width="200" height="133" loading="lazy">' +
      '</div>';
    }

    return (
      '<div class="' + cardClass + '">' +
        '<div class="product-card__media relative h-auto">' +
          '<a class="block relative media media--square" href="' + href + '" tabindex="-1">' +
            (imgSrc ? '<img src="' + imgSrc + '" alt="' + imgAlt.replace(/"/g, '&quot;') + '">' : '') +
          '</a>' +
          nsfwOverlayHtml +
        '</div>' +
        '<div class="product-card__content grow flex flex-col justify-between text-left">' +
          '<div class="product-card__top w-full"></div>' +
          '<div class="product-card__details flex flex-col items-baseline gap-2 w-full h-full">' +
            '<div class="product-card__title-wrapper grow">' +
              (vendor ? '<div class="product-card__vendor" style="color:#6b7280; font-size: 0.85em; margin-bottom: 2px;">' + String(vendor).replace(/</g, '&lt;') + '</div>' : '') +
              '<a class="product-card__title text-base-xl font-medium leading-tight" href="' + href + '">' + title.replace(/</g, '&lt;') + '</a>' +
            '</div>' +
            buttonsHtml +
          '</div>' +
        '</div>' +
      '</div>'
    );
  };

  const formatWidgetSlides = (widgetRootEl, id) => {
    const g = ensureGlobal();
    try {
      const list = widgetRootEl.querySelector('.rebuy-product-grid .splide__list');
      if (!list) return;
      const blocks = list.querySelectorAll('.rebuy-product-block');
      const formatted = [];
      blocks.forEach((b) => {
        if (b.getAttribute('data-formatted') === 'true') return;
        const html = buildFormattedCard(b, id);
        formatted.push(html);
        b.removeAttribute('style');
        b.innerHTML = html;
        b.setAttribute('data-formatted', 'true');

        const addToCartBtns = b.querySelectorAll('.rebuy-add-to-cart-btn');
        addToCartBtns.forEach((btn) => {
          const variantId = btn.getAttribute('data-variant-id');
          if (variantId) {
            btn.addEventListener('click', (e) => {
              e.preventDefault();
              addToCart(variantId, btn);
            });
          }
        });
      });
      g.byWidgetId[id] = { ...(g.byWidgetId[id] || {}), formattedCards: formatted };

      try {
        const track = widgetRootEl.querySelector('.rebuy-product-grid .splide__track');
        if (track) {
          track.style.setProperty('overflow-x', 'auto', 'important');
          track.style.setProperty('overflow-y', 'hidden', 'important');
          track.style.setProperty('-webkit-overflow-scrolling', 'touch');
          track.style.setProperty('touch-action', 'pan-x pan-y');
          track.style.setProperty('scroll-behavior', 'smooth');
          track.style.setProperty('scrollbar-width', 'none', 'important');
          track.style.setProperty('-ms-overflow-style', 'none', 'important');
        }
        const list = widgetRootEl.querySelector('.rebuy-product-grid .splide__list');
        if (list) {
          list.style.setProperty('display', 'flex', 'important');
          list.style.setProperty('flex-wrap', 'nowrap', 'important');
        }
      } catch (e) {
        console.warn('[Rebuy] enable scroll error:', e);
      }

      try {
        const splide = widgetRootEl.querySelector('.rebuy-product-grid.splide');
        const track = widgetRootEl.querySelector('.rebuy-product-grid .splide__track');
        let arrowsContainer = widgetRootEl.querySelector('.rebuy-product-grid .splide__arrows, .rebuy-product-grid .rebuy-carousel__arrows');
        let nextBtn = widgetRootEl.querySelector('.rebuy-product-grid .rebuy-carousel__arrow--next, .rebuy-product-grid .splide__arrow--next');
        let prevBtn = widgetRootEl.querySelector('.rebuy-product-grid .rebuy-carousel__arrow--prev, .rebuy-product-grid .splide__arrow--prev');

        if (track && splide) {
          const scrollStep = Math.max(0, Math.round(window.innerWidth * 0.4));
          const trackId = track.id || 'splide-track';

          if (!arrowsContainer) {
            arrowsContainer = document.createElement('div');
            arrowsContainer.className = 'splide__arrows rebuy-carousel__arrows splide__arrows--ltr';
            splide.appendChild(arrowsContainer);
          }

          if (!nextBtn) {
            nextBtn = document.createElement('button');
            nextBtn.className = 'splide__arrow rebuy-carousel__arrow splide__arrow--next rebuy-carousel__arrow--next';
            nextBtn.type = 'button';
            nextBtn.setAttribute('aria-label', 'Next slide');
            nextBtn.setAttribute('aria-controls', trackId);
            nextBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40" focusable="false"><path d="m15.5 0.932-4.3 4.38 14.5 14.6-14.5 14.5 4.3 4.4 14.6-14.6 4.4-4.3-4.4-4.4-14.6-14.6z"></path></svg>';
            arrowsContainer.appendChild(nextBtn);
          }

          if (!prevBtn) {
            prevBtn = document.createElement('button');
            prevBtn.className = 'splide__arrow rebuy-carousel__arrow splide__arrow--prev rebuy-carousel__arrow--prev';
            prevBtn.type = 'button';
            prevBtn.setAttribute('aria-label', 'Go to last slide');
            prevBtn.setAttribute('aria-controls', trackId);
            prevBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40" focusable="false"><path d="m15.5 0.932-4.3 4.38 14.5 14.6-14.5 14.5 4.3 4.4 14.6-14.6 4.4-4.3-4.4-4.4-14.6-14.6z"></path></svg>';
            arrowsContainer.insertBefore(prevBtn, nextBtn);
          }

          if (nextBtn) {
            const newNextBtn = nextBtn.cloneNode(true);
            nextBtn.parentNode.replaceChild(newNextBtn, nextBtn);
            newNextBtn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              if (track) {
                const maxScroll = track.scrollWidth - track.clientWidth;
                const currentScroll = track.scrollLeft || 0;
                const newScroll = Math.min(currentScroll + scrollStep, maxScroll);
                track.scrollTo({ left: newScroll, behavior: 'smooth' });
              }
            }, { passive: false, capture: true });
          }

          if (prevBtn) {
            const newPrevBtn = prevBtn.cloneNode(true);
            prevBtn.parentNode.replaceChild(newPrevBtn, prevBtn);
            newPrevBtn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              if (track) {
                const currentScroll = track.scrollLeft || 0;
                const newScroll = Math.max(0, currentScroll - scrollStep);
                track.scrollTo({ left: newScroll, behavior: 'smooth' });
              }
            }, { passive: false, capture: true });
          }
        }
      } catch (e) {
        console.warn('[Rebuy] custom arrow scroll error:', e);
      }

      widgetRootEl.style.display = 'block';
      widgetRootEl.removeAttribute('data-formatting');
    } catch (err) {
      console.error('[Rebuy] formatWidgetSlides error:', err);
      widgetRootEl.style.display = 'none';
      widgetRootEl.removeAttribute('data-formatting');
    }
  };

  const replacePrimaryTitleTag = (widgetRootEl) => {
    try {
      widgetRootEl.querySelectorAll('h4.primary-title, h3.primary-title').forEach((h4) => {
        const h2 = document.createElement('h2');
        h2.className = h4.className;
        h2.innerHTML = h4.innerHTML;
        if (h4.parentNode) h4.parentNode.replaceChild(h2, h4);
      });
    } catch (err) {
      debugLog('[Rebuy] replacePrimaryTitleTag error:', err);
    }
  };

  const applyComparePriceStyles = () => {
    try {
      const compareElements = document.querySelectorAll('.rebuy-widget.rebuy-custom-ui .product-card__buttons .price__compare');
      compareElements.forEach((el) => {
        el.style.setProperty('color', 'white', 'important');
      });

      const bundlePriceElements = document.querySelectorAll('.rebuy-custom-frequently-bought-together-ui .rebuy-bundle__actions-price span > span:nth-child(2)');
      bundlePriceElements.forEach((el) => {
        el.style.setProperty('color', '#fa4e70', 'important');
      });

      const bundleCompareAtElements = document.querySelectorAll('.rebuy-custom-frequently-bought-together-ui .rebuy-bundle__actions-price .compare-at span:nth-child(2)');
      bundleCompareAtElements.forEach((el) => {
        el.style.setProperty('color', '#ffffff', 'important');
      });

      const frequentlyBoughtMoneyElements = document.querySelectorAll('.rebuy-custom-frequently-bought-together-ui .rebuy-money');
      frequentlyBoughtMoneyElements.forEach((el) => {
        el.style.setProperty('color', '#fa4e70', 'important');
      });
      const frequentlyBoughtSinglePrice = document.querySelectorAll('.rebuy-custom-frequently-bought-together-ui .rebuy-bundle__actions-price-value .rebuy-money');
      frequentlyBoughtSinglePrice.forEach((el) => {
        const priceSpans = el.querySelectorAll('span:nth-child(2)');
        if (priceSpans.length === 1 && !el.classList.contains('sale') && !el.classList.contains('compare-at')) {
          priceSpans[0].style.setProperty('color', '#ffffff', 'important');
        }
      });

      const widgetMoneyElements = document.querySelectorAll('.rebuy-widget .rebuy-money');
      widgetMoneyElements.forEach((el) => {
        el.style.setProperty('color', '#fa4e70', 'important');
      });

      const bundleButtonElements = document.querySelectorAll('.rebuy-custom-frequently-bought-together-ui .rebuy-bundle .rebuy-bundle__actions .rebuy-button');
      bundleButtonElements.forEach((el) => {
        el.style.setProperty('background-color', '#fa4e70', 'important');
      });
    } catch (err) {
      console.error('[Rebuy] applyComparePriceStyles error:', err);
    }
  };

  const replaceMobileWidgetButtons = () => {
    if (!window.matchMedia('(max-width: 768px)').matches) return;

    document.querySelectorAll('.widget-type-product .rebuy-button').forEach((button) => {
      if (button.dataset.mobileIconApplied === 'true') return;
      button.dataset.mobileIconApplied = 'true';
      button.innerHTML = `
        <svg class="icon icon-cart icon-sm md:hidden" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:20px; height:20px;">
          <path stroke-miterlimit="10" d="M7.1802 7.58C7.1802 4.36 9.2402 1.75 11.7902 1.75C14.3402 1.75 16.4002 4.36 16.4002 7.58M11.7902 7.58C2.0402 7.58 0.690197 6.97 2.3502 16.18C3.3002 21.48 3.9802 22.22 11.7902 22.22C19.6002 22.22 20.2802 21.47 21.2302 16.18C22.8902 6.97 21.5502 7.58 11.7902 7.58Z"></path>
        </svg>
      `;
    });
  };

  // Keep Vue's price containers and controls intact. Only update their v-html
  // money leaves; the existing theme-owned image captions use the same values.
  const updateBundleMoney = (container, price, comparePrice) => {
    if (!container) return;
    container.querySelectorAll('.rebuy-money').forEach(money => {
      const compare = money.classList.contains('compare-at');
      money.hidden = compare && comparePrice <= price;
      const value = money.querySelector(':scope > span:not(.sr-only)');
      if (!value) return;
      const html = formatMarketAmount(compare ? comparePrice : price);
      if (value.innerHTML !== html) {
        // Compare browser-normalized markup too (for example &yen; versus ¥),
        // so observing our own writes cannot create a perpetual scan loop.
        const template = document.createElement('template');
        template.innerHTML = html;
        if (value.innerHTML !== template.innerHTML) value.innerHTML = template.innerHTML;
      }
    });
  };

  const insertBundleTitleAndPriceBelowImages = () => {
    if (!document.body?.classList.contains('template-product')) return;
    if (typeof window.theme?.Currency?.formatMoney !== 'function') return;

    Object.values(ensureGlobal().bundleWidgets).forEach(widget => {
      const root = document.getElementById('rebuy-widget-' + widget.id);
      const pricing = getBundlePrices(widget);
      if (!root || !pricing) return;

      pricing.items.forEach(({ product, price, comparePrice }) => {
        const block = root.querySelector('.rebuy-product-block.product-id-' + product.id);
        const priceEl = block?.querySelector('.rebuy-product-price');
        updateBundleMoney(priceEl, price, comparePrice);

        const imageItem = root.querySelector('.rebuy-bundle__image.product-id-' + product.id + ' .rebuy-bundle__image-item');
        const img = imageItem?.querySelector('img');
        if (!img || !priceEl) return;

        let titleDiv = imageItem.querySelector('.injected-title');
        if (!titleDiv) {
          titleDiv = document.createElement('div');
          titleDiv.className = 'injected-title';
          Object.assign(titleDiv.style, {
            marginTop: '8px', fontSize: '14px', color: '#333', textAlign: 'center',
            whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: '100%', lineHeight: '1.4', display: 'block'
          });
          img.insertAdjacentElement('afterend', titleDiv);
        }
        const title = block.querySelector('.rebuy-product-title')?.textContent.trim() || product.title;
        if (titleDiv.textContent !== title) titleDiv.textContent = title;

        let priceDiv = imageItem.querySelector('.injected-price');
        if (!priceDiv) {
          priceDiv = document.createElement('div');
          priceDiv.className = 'injected-price';
          Object.assign(priceDiv.style, {
            fontSize: '13px', color: '#666', textAlign: 'center', marginTop: '7px', marginBottom: '16px', display: 'block'
          });
          titleDiv.insertAdjacentElement('afterend', priceDiv);
        }
        if (priceDiv.innerHTML !== priceEl.innerHTML) priceDiv.innerHTML = priceEl.innerHTML;
      });
      updateBundleMoney(root.querySelector('.rebuy-bundle__actions-price-value'), pricing.total, pricing.compareTotal);
    });
  };

  const startWidgetWatcher = () => {
    // The async Rebuy embed can finish before this deferred theme asset loads.
    // Register mounted widgets as well as those delivered by lifecycle events.
    (window.Rebuy?.widgets || []).forEach(parseWidgetInfo);
    const g = ensureGlobal();
    if (g._widgetWatcher) {
      if (typeof g._scheduleWidgetScan === 'function') {
        g._scheduleWidgetScan();
      }
      return;
    }

    let scanQueued = false;

    const scan = () => {
      scanQueued = false;
      try {
        const nodes = document.querySelectorAll('.rebuy-widget[id^="rebuy-widget-"]');
        nodes.forEach((el) => {
          replacePrimaryTitleTag(el);
          const idAttr = el.getAttribute('id');
          if (!idAttr || !idAttr.startsWith('rebuy-widget-')) return;
          const id = idAttr.replace('rebuy-widget-', '');
          if (!g.allowedWidgetIds[id]) return;
          const products = g.byWidgetId[id]?.products || [];
          if (!products.length || !products.every(p => marketPrices.get(marketPriceKey(p))?.product)) return;
          if (typeof window.theme?.Currency?.formatMoney !== 'function') return;

          const html = typeof el.innerHTML === 'string' ? el.innerHTML : '';
          if (!id) return;
          const prev = g._lastHtml[id];
          if (html && html !== prev) {
            el.classList.add('rebuy-custom-ui');
            g.byWidgetId[id] = { ...(g.byWidgetId[id] || {}), html };
            g._lastHtml[id] = html;
            el.setAttribute('data-formatting', 'true');
            el.style.display = 'none';
            formatWidgetSlides(el, id);
            applyComparePriceStyles();
          }
        });
        replaceMobileWidgetButtons();
        insertBundleTitleAndPriceBelowImages();
      } catch (err) {
        console.error('[Rebuy] widget scan error:', err);
      }
    };

    const scheduleScan = () => {
      if (scanQueued) return;
      scanQueued = true;

      const run = () => scan();
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(run, { timeout: 500 });
      } else {
        window.requestAnimationFrame(run);
      }
    };

    g._scheduleWidgetScan = scheduleScan;
    scheduleScan();

    const observerRoot = document.querySelector('main') || document.body || document.documentElement;
    const observer = new MutationObserver(scheduleScan);
    observer.observe(observerRoot, { childList: true, characterData: true, subtree: true });
    g._widgetWatcher = observer;

    [
      'rebuy.beforeReady',
      'rebuy.ready',
      'rebuy:widget.ready',
      'rebuy:cart:change',
      'rebuy:smartcart.ready',
      'rebuy:smartcart.show'
    ].forEach((eventName) => {
      document.addEventListener(eventName, scheduleScan);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startWidgetWatcher);
  } else {
    startWidgetWatcher();
  }
})();
